import { create } from 'zustand';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  severity: ToastSeverity;
}

interface ToastState {
  toasts: Toast[];
  addToast: (message: string, severity?: ToastSeverity) => void;
  removeToast: (id: number) => void;
}

let nextId = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, severity = 'success') => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, message, severity }] }));
  },
  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Convenience hook for showing toasts from components */
export function useToast() {
  const addToast = useToastStore((s) => s.addToast);
  return {
    success: (message: string) => addToast(message, 'success'),
    error: (message: string) => addToast(message, 'error'),
    warning: (message: string) => addToast(message, 'warning'),
    info: (message: string) => addToast(message, 'info'),
  };
}

/**
 * Non-hook version for use outside React components (e.g. MutationCache callbacks).
 * Calls the store directly.
 */
export const toast = {
  success: (message: string) =>
    useToastStore.getState().addToast(message, 'success'),
  error: (message: string) =>
    useToastStore.getState().addToast(message, 'error'),
  warning: (message: string) =>
    useToastStore.getState().addToast(message, 'warning'),
  info: (message: string) => useToastStore.getState().addToast(message, 'info'),
};
